'use client';

import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { Bell, BellOff, Settings, Share, Smartphone } from 'lucide-react';
import { useEffect } from 'react';

interface NotificationPermissionProps {
  onTokenReceived?: (token: string) => void;
}

export default function NotificationPermission({ onTokenReceived }: NotificationPermissionProps) {
  const { permission, token, isLoading, isIOSNonPWA, requestPermission } = useNotifications();

  useEffect(() => {
    if (token && onTokenReceived) {
      onTokenReceived(token);
    }
  }, [token, onTokenReceived]);

  const handleRequestPermission = async () => {
    await requestPermission();
  };

  if (permission === 'granted' && token) {
    return (
      <div className='flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4'>
        <Bell className='h-5 w-5 text-emerald-600' />
        <div className='flex-1'>
          <p className='text-sm font-medium text-emerald-800'>Notificações ativadas</p>
          <p className='text-xs text-emerald-600'>Você receberá notificações push neste dispositivo</p>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className='flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4'>
        <BellOff className='h-5 w-5 text-red-600' />
        <div className='flex-1'>
          <p className='text-sm font-medium text-red-800'>Notificações bloqueadas</p>
          <p className='text-xs text-red-600'>Ative nas configurações do navegador para receber notificações</p>
        </div>
        <Settings className='h-4 w-4 text-red-500' />
      </div>
    );
  }

  if (isIOSNonPWA) {
    return (
      <div className='flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4'>
        <Smartphone className='mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600' />
        <div className='flex-1'>
          <p className='text-sm font-medium text-blue-800'>Para receber notificações no iPhone:</p>
          <ol className='mt-2 space-y-1 text-xs text-blue-700'>
            <li className='flex items-center gap-1.5'>
              <span>1.</span>
              <span>
                Toque no ícone de compartilhar <Share className='inline h-3 w-3' />
              </span>
            </li>
            <li>
              <span>2.</span> Selecione &quot;Adicionar à Tela de Início&quot;
            </li>
            <li>
              <span>3.</span> Abra o app pela Tela de Início
            </li>
            <li>
              <span>4.</span> Ative as notificações
            </li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4'>
      <Bell className='h-5 w-5 text-blue-600' />
      <div className='flex-1'>
        <p className='text-sm font-medium text-blue-800'>Ativar notificações push</p>
        <p className='text-xs text-blue-600'>Receba notificações importantes diretamente no navegador</p>
      </div>
      <Button
        onClick={handleRequestPermission}
        disabled={isLoading}
        variant='outline-primary'
        className='h-8 px-3 text-xs'
        isLoading={isLoading}>
        {isLoading ? 'Ativando...' : 'Ativar'}
      </Button>
    </div>
  );
}
